import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';
import DiscordModal from './DiscordModal';

// ── Helpers ────────────────────────────────────────────────────

const ROLE_ORDER = ['ADMIN','UPDATER','FORCE_ONE','BRUTE_FORCE','MIDLAND','SUPERNOVA','RUSSIA','FRIEND','GUEST','OTHER'];
const ALL_ROLES  = ROLE_ORDER;

function badge(role) {
    const colors = {
        ADMIN:       { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
        UPDATER:     { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
        FORCE_ONE:   { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
        BRUTE_FORCE: { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
    };
    const s = colors[role] || { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px',
            borderRadius: 99, fontSize: 11, fontWeight: 600,
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>{role}</span>
    );
}

// ── Trigger Section ────────────────────────────────────────────

function TriggerSection() {
    const [season, setSeason]   = useState('');
    const [status, setStatus]   = useState({});

    const trigger = async (key, path) => {
        setStatus(s => ({ ...s, [key]: { loading: true, msg: null, err: null } }));
        try {
            const res = await api.post(path);
            setStatus(s => ({ ...s, [key]: { loading: false, msg: res.data, err: null } }));
        } catch (e) {
            const msg = e.response?.data?.message || e.response?.data || e.message || 'Error';
            setStatus(s => ({ ...s, [key]: { loading: false, msg: null, err: String(msg) } }));
        }
    };

    const s = (key) => status[key] || {};

    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header">Manual Triggers</div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                <TriggerRow
                    label="Update Today"
                    desc="Refreshes current race: track, weather, season, race number"
                    loading={s('today').loading}
                    msg={s('today').msg}
                    err={s('today').err}
                    onTrigger={() => trigger('today', '/management/trigger/today')}
                />

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingTop: 4 }}>
                    <div style={{ flex: 1 }}>
                        <TriggerRow
                            label="Refresh Calendar"
                            desc="Deletes and reloads season calendar. Use optional season override."
                            loading={s('calendar').loading}
                            msg={s('calendar').msg}
                            err={s('calendar').err}
                            onTrigger={() => {
                                const path = season
                                    ? `/management/trigger/calendar?season=${season}`
                                    : '/management/trigger/calendar';
                                trigger('calendar', path);
                            }}
                        />
                    </div>
                    <div style={{ paddingTop: 22 }}>
                        <input
                            type="number"
                            placeholder="Season"
                            value={season}
                            onChange={e => setSeason(e.target.value)}
                            style={{
                                width: 90, padding: '7px 10px',
                                border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)',
                                fontSize: 13, background: '#fff', color: 'var(--c-text)',
                            }}
                        />
                    </div>
                </div>

                <TriggerRow
                    label="Update Tyres"
                    desc="Fetches tyre supplier parameters from GPRO API"
                    loading={s('tyres').loading}
                    msg={s('tyres').msg}
                    err={s('tyres').err}
                    onTrigger={() => trigger('tyres', '/management/trigger/tyres')}
                />
            </div>
        </div>
    );
}

function TriggerRow({ label, desc, loading, msg, err, onTrigger }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--c-border-light)' }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text)', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{desc}</div>
                {msg && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--c-success)', fontWeight: 500 }}>{String(msg)}</div>}
                {err && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--c-error)', fontWeight: 500 }}>{err}</div>}
            </div>
            <button
                onClick={onTrigger}
                disabled={loading}
                style={{
                    padding: '7px 18px', borderRadius: 'var(--radius-sm)',
                    background: loading ? '#e5e7eb' : 'var(--c-accent)',
                    color: loading ? 'var(--c-text-muted)' : '#fff',
                    border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                    transition: 'background 0.15s',
                }}
            >
                {loading ? 'Running…' : 'Run'}
            </button>
        </div>
    );
}

// ── Migration Section ──────────────────────────────────────────

function MigrationSection() {
    const [result, setResult]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState(null);
    const [confirm, setConfirm] = useState(false);

    const run = async () => {
        setLoading(true); setErr(null); setResult(null); setConfirm(false);
        try {
            const res = await api.post('/admin/migrate/consolidate-users');
            setResult(res.data);
        } catch (e) {
            setErr(e.response?.data?.message || e.message || 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header">User Migration</div>
            <div style={{ padding: '16px 20px' }}>
                <p style={{ fontSize: 13, color: 'var(--c-text-2)', marginBottom: 12 }}>
                    One-time migration: merges duplicate accounts (login ending in 2, 3, or 4) into the primary account. Deletes duplicate users after merging all history.
                </p>
                {!confirm ? (
                    <button
                        onClick={() => setConfirm(true)}
                        style={{
                            padding: '7px 18px', borderRadius: 'var(--radius-sm)',
                            background: '#fef2f2', color: '#dc2626',
                            border: '1px solid #fecaca', cursor: 'pointer',
                            fontSize: 13, fontWeight: 600,
                        }}
                    >
                        Run Migration
                    </button>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 13, color: 'var(--c-error)', fontWeight: 500 }}>Are you sure? This is irreversible.</span>
                        <button
                            onClick={run}
                            disabled={loading}
                            style={{
                                padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                                background: '#dc2626', color: '#fff',
                                border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            }}
                        >
                            {loading ? 'Running…' : 'Confirm'}
                        </button>
                        <button
                            onClick={() => setConfirm(false)}
                            style={{
                                padding: '7px 14px', borderRadius: 'var(--radius-sm)',
                                background: '#f9fafb', color: 'var(--c-text-muted)',
                                border: '1px solid var(--c-border)', cursor: 'pointer', fontSize: 13,
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                )}
                {err && <div style={{ marginTop: 10, color: 'var(--c-error)', fontSize: 13 }}>{err}</div>}
                {result && (
                    <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.7, color: 'var(--c-text-2)' }}>
                        <div><b>Remapped:</b> {result.remappedCount} &nbsp; <b>Skipped:</b> {result.skippedCount}</div>
                        {result.remapped?.length > 0 && (
                            <div><b>Remapped:</b> {result.remapped.join(', ')}</div>
                        )}
                        {result.skipped?.length > 0 && (
                            <div><b>Skipped:</b> {result.skipped.join(', ')}</div>
                        )}
                        {result.deletedUsers?.length > 0 && (
                            <div><b>Deleted:</b> {result.deletedUsers.join(', ')}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── User Edit Modal ────────────────────────────────────────────

function EditUserModal({ user, onClose, onSaved }) {
    const [form, setForm] = useState({
        firstname:  user.firstname  || '',
        lastname:   user.lastname   || '',
        email:      user.email      || '',
        role:       user.role       || '',
        gprotoken:  user.gprotoken  || '',
        password:   '',
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState(null);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        setLoading(true); setErr(null);
        const body = {};
        if (form.firstname)  body.firstname  = form.firstname;
        if (form.lastname)   body.lastname   = form.lastname;
        if (form.email)      body.email      = form.email;
        if (form.role)       body.role       = form.role;
        if (form.gprotoken)  body.gprotoken  = form.gprotoken;
        if (form.password)   body.password   = form.password;
        try {
            const res = await api.put(`/management/users/${user.id}`, body);
            onSaved(res.data);
        } catch (e) {
            setErr(e.response?.data?.message || e.message || 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: 'var(--radius-lg)',
                padding: 24, width: 420, maxWidth: '95vw',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }} onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                    Edit User #{user.id} — {user.email}
                </div>

                {[
                    ['firstname',  'First Name',    'text'],
                    ['lastname',   'Last Name',     'text'],
                    ['email',      'Login',         'text'],
                    ['gprotoken',  'GPRO Token',    'text'],
                    ['password',   'New Password',  'password'],
                ].map(([key, label, type]) => (
                    <div key={key} style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 4 }}>{label}</label>
                        <input
                            type={type}
                            value={form[key]}
                            onChange={e => set(key, e.target.value)}
                            style={{
                                width: '100%', padding: '7px 10px',
                                border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)',
                                fontSize: 13, color: 'var(--c-text)',
                            }}
                        />
                    </div>
                ))}

                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 4 }}>Role</label>
                    <select
                        value={form.role}
                        onChange={e => set('role', e.target.value)}
                        style={{
                            width: '100%', padding: '7px 10px',
                            border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)',
                            fontSize: 13, color: 'var(--c-text)', background: '#fff',
                        }}
                    >
                        {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                {err && <div style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{
                        padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                        background: '#f9fafb', border: '1px solid var(--c-border)',
                        color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 13,
                    }}>Cancel</button>
                    <button onClick={save} disabled={loading} style={{
                        padding: '7px 18px', borderRadius: 'var(--radius-sm)',
                        background: 'var(--c-accent)', border: 'none',
                        color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>{loading ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
}

// ── GPRO Modal ─────────────────────────────────────────────────

const SETTING_COLOR = { lime: '#16a34a', orange: '#d97706', red: '#dc2626' };

const SettingCell = ({ s }) => {
    if (!s || s.value == null) return <td style={{ padding: '6px 8px', color: 'var(--c-text-light)' }}>—</td>;
    const color = SETTING_COLOR[s.color] || 'inherit';
    return (
        <td style={{ padding: '6px 8px' }} title={s.comment || undefined}>
            <span style={{ color, fontWeight: s.color ? 600 : 400, cursor: s.comment ? 'help' : 'default' }}>
                {s.value}{s.comment ? ' ·' : ''}
            </span>
        </td>
    );
};

function GproModal({ user, onClose }) {
    const [data, setData]             = useState(null);
    const [loading, setLoading]       = useState(true);
    const [err, setErr]               = useState(null);
    const [expandedLaps, setExpanded] = useState(new Set());

    const toggleLap = (idx) => setExpanded(prev => {
        const next = new Set(prev);
        next.has(idx) ? next.delete(idx) : next.add(idx);
        return next;
    });

    useEffect(() => {
        api.get(`/management/users/${user.id}/gpro`)
            .then(r => setData(r.data))
            .catch(e => setErr(e.response?.data?.error || e.message || 'Error'))
            .finally(() => setLoading(false));
    }, [user.id]);

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: 'var(--radius-lg)',
                padding: 24, width: 480, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }} onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                    GPRO Data — {user.email}
                </div>

                {loading && <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
                {err && <div style={{ color: 'var(--c-error)', fontSize: 13 }}>{err}</div>}
                {data && (
                    <>
                        {data.error && <div style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 10 }}>{data.error}</div>}

                        {/* Tyre supplier + API remaining */}
                        {data.tyreSupplierName && (
                            <div style={{ marginBottom: 12, fontSize: 13 }}>
                                <b>Tyre Supplier:</b> {data.tyreSupplierName}
                                {data.apiRequestsRemaining != null && (
                                    <span style={{ marginLeft: 12, color: 'var(--c-text-muted)' }}>API left: {data.apiRequestsRemaining}</span>
                                )}
                            </div>
                        )}

                        {/* Practice laps */}
                        {data.practiceLaps?.length > 0 && (
                            <>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--c-text-muted)', marginBottom: 6 }}>Practice Laps</div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb' }}>
                                                {['#','Lap Time','Net','Mis','Tyres','FW','RW','Eng','Brk','Gear','Susp'].map(h => (
                                                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--c-border)', color: 'var(--c-text-muted)', fontWeight: 600 }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.practiceLaps.map(lap => {
                                                const hasComments = lap.driverComments?.length > 0;
                                                const isOpen = expandedLaps.has(lap.idx);
                                                return (
                                                    <React.Fragment key={lap.idx}>
                                                        <tr style={{ borderBottom: isOpen ? 'none' : '1px solid var(--c-border-light)', background: isOpen ? '#fafffe' : 'transparent' }}>
                                                            <td style={{ padding: '6px 8px', color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>
                                                                {hasComments ? (
                                                                    <button onClick={() => toggleLap(lap.idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--c-text-muted)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                        <span style={{ fontSize: 10 }}>{isOpen ? '▴' : '▾'}</span>{lap.idx}
                                                                    </button>
                                                                ) : lap.idx}
                                                            </td>
                                                            <td style={{ padding: '6px 8px', color: lap.lapTimeColor === 'lime' ? '#16a34a' : 'inherit', fontWeight: lap.lapTimeColor === 'lime' ? 700 : 400 }}>{lap.lapTime}</td>
                                                            <td style={{ padding: '6px 8px' }}>{lap.netTime}</td>
                                                            <td style={{ padding: '6px 8px', color: 'var(--c-text-muted)' }}>{lap.misTime}</td>
                                                            <td style={{ padding: '6px 8px' }}>{lap.setTyres ?? '—'}</td>
                                                            <SettingCell s={lap.fwing}  />
                                                            <SettingCell s={lap.rwing}  />
                                                            <SettingCell s={lap.engine} />
                                                            <SettingCell s={lap.brakes} />
                                                            <SettingCell s={lap.gear}   />
                                                            <SettingCell s={lap.susp}   />
                                                        </tr>
                                                        {hasComments && isOpen && (
                                                            <tr style={{ borderBottom: '1px solid var(--c-border-light)', background: '#fafffe' }}>
                                                                <td colSpan={11} style={{ padding: '6px 12px 10px 20px' }}>
                                                                    {lap.driverComments.map((c, i) => (
                                                                        <div key={i} style={{ fontSize: 12, color: 'var(--c-text-2)', lineHeight: 1.5, marginBottom: i < lap.driverComments.length - 1 ? 3 : 0 }}>
                                                                            <span style={{ fontWeight: 600, color: 'var(--c-text)' }}>{c.part}:</span> {c.text}
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        {data.practiceLaps?.length === 0 && !data.error && (
                            <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>Practice not started</div>
                        )}
                    </>
                )}

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <button onClick={onClose} style={{
                        padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                        background: '#f9fafb', border: '1px solid var(--c-border)',
                        color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 13,
                    }}>Close</button>
                </div>
            </div>
        </div>
    );
}

// ── Create User Modal ──────────────────────────────────────────

function CreateUserModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ email: '', password: '', role: 'FORCE_ONE', firstname: '', lastname: '', gprotoken: '' });
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState(null);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.email || !form.password || !form.role) {
            setErr('Email, password and role are required.');
            return;
        }
        setLoading(true); setErr(null);
        const body = { email: form.email, password: form.password, role: form.role };
        if (form.firstname)  body.firstname  = form.firstname;
        if (form.lastname)   body.lastname   = form.lastname;
        if (form.gprotoken)  body.gprotoken  = form.gprotoken;
        try {
            const res = await api.post('/management/users', body);
            onCreated(res.data);
        } catch (e) {
            if (e.response?.status === 409) setErr('Login already taken.');
            else setErr(e.response?.data?.message || e.message || 'Error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', padding: 24, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>New User</div>

                {[
                    ['email',     'Login *',       'text'    ],
                    ['password',  'Password *',    'password'],
                    ['firstname', 'First Name',    'text'    ],
                    ['lastname',  'Last Name',     'text'    ],
                    ['gprotoken', 'GPRO Token',    'text'    ],
                ].map(([key, label, type]) => (
                    <div key={key} style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 4 }}>{label}</label>
                        <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--c-text)' }} />
                    </div>
                ))}

                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 4 }}>Role *</label>
                    <select value={form.role} onChange={e => set('role', e.target.value)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--c-text)', background: '#fff' }}>
                        {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                {err && <div style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', background: '#f9fafb', border: '1px solid var(--c-border)', color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    <button onClick={save} disabled={loading} style={{ padding: '7px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--c-accent)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{loading ? 'Creating…' : 'Create'}</button>
                </div>
            </div>
        </div>
    );
}

// ── Users Section ──────────────────────────────────────────────

function UsersSection() {
    const [users, setUsers]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [err, setErr]           = useState(null);
    const [editUser,   setEditUser]   = useState(null);
    const [gproUser,   setGproUser]   = useState(null);
    const [showCreate, setShowCreate] = useState(false);

    const load = () => {
        setLoading(true); setErr(null);
        api.get('/management/users')
            .then(r => setUsers(r.data))
            .catch(e => setErr(e.response?.data?.message || e.message || 'Error'))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const onSaved = (updated) => {
        setUsers(us => us.map(u => u.id === updated.id ? updated : u));
        setEditUser(null);
    };

    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Users</span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowCreate(true)} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--c-accent)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ New User</button>
                    <button onClick={load} style={{ padding: '4px 12px', borderRadius: 'var(--radius-sm)', background: '#f9fafb', border: '1px solid var(--c-border)', color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 12 }}>Refresh</button>
                </div>
            </div>

            {loading && <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--c-text-muted)' }}>Loading…</div>}
            {err && <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--c-error)' }}>{err}</div>}

            {!loading && !err && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                {['ID','Name','Login','Role','API left','Last Activity',''].map(h => (
                                    <th key={h} style={{
                                        padding: '8px 14px', textAlign: 'left',
                                        borderBottom: '1px solid var(--c-border)',
                                        color: 'var(--c-text-muted)', fontWeight: 600,
                                        whiteSpace: 'nowrap', fontSize: 12,
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id} style={{ borderBottom: '1px solid var(--c-border-light)' }}>
                                    <td style={{ padding: '8px 14px', color: 'var(--c-text-muted)' }}>{u.id}</td>
                                    <td style={{ padding: '8px 14px', fontWeight: 500 }}>{u.firstname} {u.lastname}</td>
                                    <td style={{ padding: '8px 14px', fontFamily: 'monospace' }}>{u.email}</td>
                                    <td style={{ padding: '8px 14px' }}>{badge(u.role)}</td>
                                    <td style={{ padding: '8px 14px', textAlign: 'center', color: u.apiRequestsRemaining < 20 ? '#dc2626' : u.apiRequestsRemaining < 50 ? '#d97706' : 'var(--c-text-2)' }}>
                                        {u.apiRequestsRemaining ?? '—'}
                                    </td>
                                    <td style={{ padding: '8px 14px', color: 'var(--c-text-muted)', fontSize: 12 }}>
                                        {u.lastActivity ? new Date(u.lastActivity).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                                    </td>
                                    <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                        <button onClick={() => setGproUser(u)} style={{
                                            padding: '4px 10px', marginRight: 6,
                                            borderRadius: 'var(--radius-sm)', fontSize: 12,
                                            background: '#eff6ff', color: '#2563eb',
                                            border: '1px solid #bfdbfe', cursor: 'pointer',
                                        }}>GPRO</button>
                                        <button onClick={() => setEditUser(u)} style={{
                                            padding: '4px 10px',
                                            borderRadius: 'var(--radius-sm)', fontSize: 12,
                                            background: '#f9fafb', color: 'var(--c-text-2)',
                                            border: '1px solid var(--c-border)', cursor: 'pointer',
                                        }}>Edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreate && (
                <CreateUserModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(u) => { setUsers(prev => [...prev, u]); setShowCreate(false); }}
                />
            )}
            {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={onSaved} />}
            {gproUser && <GproModal user={gproUser} onClose={() => setGproUser(null)} />}
        </div>
    );
}

// ── Role Visibility Matrix ─────────────────────────────────────

function VisibilitySection() {
    const [matrix,   setMatrix]   = useState(null);
    const [original, setOriginal] = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [err,      setErr]      = useState(null);
    const [saved,    setSaved]    = useState(false);

    const load = () => {
        setLoading(true); setErr(null);
        api.get('/management/role-visibility')
            .then(r => { setMatrix(r.data); setOriginal(r.data); })
            .catch(e => setErr(e.response?.data?.message || e.message || 'Error'))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const toggle = (viewer, target) => {
        if (viewer === 'ADMIN') return;
        setMatrix(prev => {
            const cur = prev[viewer] || [];
            const next = cur.includes(target) ? cur.filter(r => r !== target) : [...cur, target];
            return { ...prev, [viewer]: next };
        });
    };

    const isDirty = matrix && original && ALL_ROLES.some(role => {
        if (role === 'ADMIN') return false;
        return (matrix[role] || []).slice().sort().join(',') !== (original[role] || []).slice().sort().join(',');
    });

    const save = async () => {
        setSaving(true); setErr(null); setSaved(false);
        const changed = {};
        for (const role of ALL_ROLES) {
            if (role === 'ADMIN') continue;
            const o = (original[role] || []).slice().sort().join(',');
            const c = (matrix[role]   || []).slice().sort().join(',');
            if (o !== c) changed[role] = matrix[role];
        }
        try {
            await api.put('/management/role-visibility', changed);
            setOriginal(matrix);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            setErr(e.response?.data?.message || e.message || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Role Visibility Matrix</span>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {saved && <span style={{ fontSize: 12, color: 'var(--c-success)', fontWeight: 500 }}>Saved</span>}
                    <button onClick={save} disabled={saving || !isDirty} style={{
                        padding: '4px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, border: 'none',
                        background: isDirty ? 'var(--c-accent)' : '#e5e7eb',
                        color: isDirty ? '#fff' : 'var(--c-text-muted)',
                        cursor: isDirty ? 'pointer' : 'not-allowed',
                    }}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>

            {loading && <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--c-text-muted)' }}>Loading…</div>}
            {err     && <div style={{ padding: '8px 20px', fontSize: 13, color: 'var(--c-error)' }}>{err}</div>}

            {!loading && matrix && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                <th style={{ padding: '8px 14px', textAlign: 'left', borderBottom: '1px solid var(--c-border)', color: 'var(--c-text-muted)', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 110 }}>
                                    Viewer ↓ / Sees →
                                </th>
                                {ALL_ROLES.map(r => (
                                    <th key={r} style={{ padding: '6px 6px', textAlign: 'center', borderBottom: '1px solid var(--c-border)', color: 'var(--c-text-muted)', fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap' }}>
                                        {r.replace('_', '​_')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ALL_ROLES.map(viewer => {
                                const isAdminRow = viewer === 'ADMIN';
                                const checked = isAdminRow ? ALL_ROLES : (matrix[viewer] || []);
                                return (
                                    <tr key={viewer} style={{ borderBottom: '1px solid var(--c-border-light)', background: isAdminRow ? '#f9fafb' : 'transparent' }}>
                                        <td style={{ padding: '7px 14px', fontWeight: 600, fontSize: 12, color: isAdminRow ? 'var(--c-text-muted)' : 'var(--c-text)', whiteSpace: 'nowrap' }}>
                                            {viewer}
                                            {isAdminRow && <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 4 }}>(always all)</span>}
                                        </td>
                                        {ALL_ROLES.map(target => (
                                            <td key={target} style={{ padding: '7px 6px', textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked.includes(target)}
                                                    disabled={isAdminRow}
                                                    onChange={() => toggle(viewer, target)}
                                                    style={{ width: 14, height: 14, cursor: isAdminRow ? 'default' : 'pointer', accentColor: 'var(--c-accent)' }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--c-text-muted)' }}>
                        Empty row = viewer sees only themselves (server default)
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Site section visibility per role ───────────────────────────
function SectionAccessSection() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get('/section-access').then(r => setData(r.data)).catch(() => setErr('Failed to load'));
    }, []);
    const toggle = async (section, role, visible) => {
        setData(d => ({ ...d, matrix: { ...d.matrix, [section]: { ...(d.matrix[section] || {}), [role]: visible } } }));
        try { await api.put('/section-access', { section, role, visible }); } catch { setErr('Save failed'); }
    };
    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header">Site section visibility (per role)</div>
            {err && <div style={{ padding: '10px 16px', color: 'var(--c-error)', fontSize: 13 }}>{err}</div>}
            {data && (
                <div style={{ overflowX: 'auto', padding: 12 }}>
                    <table className="fb-table" style={{ fontSize: 12 }}>
                        <thead><tr><th>Section</th>{data.roles.map(r => <th key={r} style={{ writingMode: 'vertical-rl', textAlign: 'left' }}>{r}</th>)}</tr></thead>
                        <tbody>
                            {data.sections.map(sec => (
                                <tr key={sec}>
                                    <td style={{ fontWeight: 500 }}>{sec}</td>
                                    {data.roles.map(role => (
                                        <td key={role} style={{ textAlign: 'center' }}>
                                            <input type="checkbox" disabled={role === 'ADMIN'}
                                                checked={!!(data.matrix[sec] && data.matrix[sec][role])}
                                                onChange={e => toggle(sec, role, e.target.checked)} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>ADMIN always sees everything. Changes apply immediately (nav + route access).</p>
                </div>
            )}
        </div>
    );
}

// ── Groq (AI) access per role ──────────────────────────────────
function GroqAccessSection() {
    const [access, setAccess] = useState(null);
    useEffect(() => { api.get('/groq/access').then(r => setAccess(r.data)).catch(() => {}); }, []);
    const toggle = async (role, enabled) => {
        setAccess(a => ({ ...a, [role]: enabled }));
        try { await api.put('/groq/access', { role, enabled }); } catch { /* ignore */ }
    };
    return (
        <div className="fb-card" style={{ marginBottom: 24 }}>
            <div className="fb-card-header">Groq (AI) access per role</div>
            <div style={{ padding: 14, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                {access && Object.keys(access).map(role => (
                    <label key={role} style={{ fontSize: 13 }}>
                        <input type="checkbox" checked={!!access[role]} onChange={e => toggle(role, e.target.checked)} /> {role}
                    </label>
                ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--c-text-muted)', padding: '0 14px 12px' }}>Default off. Users with an enabled role can add their own Groq token and use AI features.</p>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────

const Admin = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';
    const [showDiscord, setShowDiscord] = useState(false);

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-text)', marginBottom: 4 }}>Admin Panel</h1>
                    <p style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>Role: {userRole}</p>
                </div>
                {isAdmin && (
                    <button onClick={() => setShowDiscord(true)} style={{ padding: '7px 16px', borderRadius: 'var(--radius-sm)', background: '#5865F2', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        Discord
                    </button>
                )}
            </div>
            {showDiscord && <DiscordModal onClose={() => setShowDiscord(false)} />}

            <TriggerSection />

            {isAdmin && (
                <>
                    <UsersSection />
                    <VisibilitySection />
                    <SectionAccessSection />
                    <GroqAccessSection />
                    <MigrationSection />
                </>
            )}
        </div>
    );
};

export default Admin;
